import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { swal } from '../utils/swal';
import './DocumentosOperador.css';

interface Documento {
  id: number;
  tipo_documento_nombre: string;
  tipo_documento_codigo?: string;
  numero_documento: string;
  fecha_emision: string;
  fecha_vencimiento: string;
  estado: string;
  archivo_path: string;
  created_at: string;
}

interface TipoDocumento {
  id: number;
  nombre: string;
  codigo: string;
}

const DocumentosOperador: React.FC = () => {
  const { user } = useAuth();
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [tiposDocumentos, setTiposDocumentos] = useState<TipoDocumento[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    tipo_documento_id: '',
    numero_documento: '',
    fecha_emision: '',
    fecha_vencimiento: '',
    observaciones: ''
  });
  const [imagenes, setImagenes] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [operadorId, setOperadorId] = useState<number | null>(null);

  useEffect(() => {
    if (user) {
      obtenerOperadorId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const obtenerOperadorId = async () => {
    try {
      // Buscar operador por email del usuario
      const response = await api.get('/operadores', {
        params: {
          email: user?.email,
          limit: 1
        }
      });
      
      const operadores = response.data.data || response.data || [];
      if (operadores.length > 0) {
        setOperadorId(operadores[0].id);
      }
    } catch (error) {
      console.error('Error obteniendo operador_id:', error);
    }
  };

  const cargarDocumentos = async () => {
    if (!operadorId) return;
    
    try {
      setLoading(true);
      const response = await api.get('/documentos', {
        params: {
          entidad_tipo: 'operador',
          entidad_id: operadorId
        }
      });
      
      // Filtrar documentos: excluir hoja de vida y acuerdo de confidencialidad
      const todosDocumentos = response.data.data || [];
      const documentosFiltrados = todosDocumentos.filter((doc: Documento) => {
        const codigo = doc.tipo_documento_codigo || '';
        const nombre = doc.tipo_documento_nombre || '';
        // Excluir hoja de vida y acuerdo
        return codigo !== 'HOJA_VIDA' && 
               codigo !== 'ACUERDO_CONFIDENCIALIDAD' &&
               !nombre.toLowerCase().includes('hoja de vida') &&
               !nombre.toLowerCase().includes('acuerdo de confidencialidad');
      });
      
      setDocumentos(documentosFiltrados);
    } catch (error) {
      console.error('Error cargando documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (operadorId) {
      cargarDocumentos();
      cargarTiposDocumentos();
    }
  }, [operadorId]);

  const cargarTiposDocumentos = async () => {
    try {
      // Filtrar solo tipos que apliquen a operadores
      const response = await api.get('/documentos/tipos', {
        params: {
          aplica_a: 'operador'
        }
      });
      
      // El endpoint puede devolver directamente el array o dentro de data
      let tipos = response.data;
      if (Array.isArray(response.data)) {
        tipos = response.data;
      } else if (response.data.data) {
        tipos = response.data.data;
      }
      
      // Filtrar para excluir hoja de vida y acuerdo de confidencialidad
      const tiposFiltrados = tipos.filter((tipo: TipoDocumento) => {
        return tipo.codigo !== 'HOJA_VIDA' && 
               tipo.codigo !== 'ACUERDO_CONFIDENCIALIDAD';
      });
      
      setTiposDocumentos(tiposFiltrados);
      console.log('Tipos de documentos cargados:', tiposFiltrados);
    } catch (error) {
      console.error('Error cargando tipos de documentos:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newImages: File[] = [];
      const newPreviews: string[] = [];

      files.forEach(file => {
        if (file.type.startsWith('image/')) {
          newImages.push(file);
          const reader = new FileReader();
          reader.onload = (e) => {
            if (e.target?.result) {
              newPreviews.push(e.target.result as string);
              setPreviews([...previews, ...newPreviews]);
            }
          };
          reader.readAsDataURL(file);
        }
      });

      setImagenes([...imagenes, ...newImages]);
    }
  };

  const removeImage = (index: number) => {
    setImagenes(imagenes.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (imagenes.length === 0) {
      await swal.warning('Debe agregar al menos una imagen');
      return;
    }

    if (!operadorId) {
      await swal.error('No se pudo identificar el operador. Por favor, contacte al administrador.');
      return;
    }

    try {
      setSubmitting(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('tipo_documento_id', formData.tipo_documento_id);
      formDataToSend.append('entidad_tipo', 'operador');
      formDataToSend.append('entidad_id', operadorId.toString());
      formDataToSend.append('numero_documento', formData.numero_documento);
      formDataToSend.append('fecha_emision', formData.fecha_emision);
      formDataToSend.append('fecha_vencimiento', formData.fecha_vencimiento);
      formDataToSend.append('observaciones', formData.observaciones);
      
      // Agregar todas las imágenes
      imagenes.forEach((imagen, index) => {
        formDataToSend.append('imagenes', imagen);
      });

      await api.post('/documentos/operador/subir', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      await swal.success('Documento creado exitosamente');
      setShowModal(false);
      resetForm();
      cargarDocumentos();
    } catch (error: any) {
      console.error('Error creando documento:', error);
      await swal.error(error.response?.data?.error || 'Error al crear documento');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_documento_id: '',
      numero_documento: '',
      fecha_emision: '',
      fecha_vencimiento: '',
      observaciones: ''
    });
    setImagenes([]);
    setPreviews([]);
  };

  const generarPDF = async (documentoId: number) => {
    try {
      const response = await api.get(`/documentos/${documentoId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documento-${documentoId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error generando PDF:', error);
      await swal.error('Error al generar PDF');
    }
  };

  const getEstadoBadge = (estado: string) => {
    const badges: { [key: string]: { text: string; class: string } } = {
      vigente: { text: 'Vigente', class: 'badge-success' },
      vencido: { text: 'Vencido', class: 'badge-danger' },
      por_vencer: { text: 'Por Vencer', class: 'badge-warning' },
      rechazado: { text: 'Rechazado', class: 'badge-danger' }
    };
    const badge = badges[estado] || { text: estado, class: 'badge-default' };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading && !operadorId) {
    return <div className="loading">Cargando información...</div>;
  }

  if (!operadorId) {
    return (
      <div className="empty-state">
        <p>No se pudo identificar su cuenta de operador. Por favor, contacte al administrador.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Cargando documentos...</div>;
  }

  const enviarUbicacion = async () => {
    if (!navigator.geolocation) {
      await swal.warning('La geolocalización no está disponible en su dispositivo');
      return;
    }

    try {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            await api.post('/ubicaciones/enviar', {
              latitud: position.coords.latitude,
              longitud: position.coords.longitude,
              precision_gps: position.coords.accuracy,
              velocidad: position.coords.speed || null,
              direccion: null
            });
            await swal.success('Ubicación enviada correctamente');
          } catch (error: any) {
            console.error('Error enviando ubicación:', error);
            await swal.error(error.response?.data?.error || 'Error al enviar ubicación');
          }
        },
        async (error) => {
          console.error('Error obteniendo ubicación:', error);
          await swal.error('Error al obtener ubicación: ' + error.message);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } catch (error) {
      console.error('Error:', error);
      await swal.error('Error al acceder a la geolocalización');
    }
  };

  return (
    <div className="documentos-operador-page">
      <div className="page-header">
        <h1>📄 Mis Documentos</h1>
        <div className="header-actions">
          <button 
            className="btn-location"
            onClick={enviarUbicacion}
            title="Enviar ubicación actual"
          >
            📍 Enviar Ubicación
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowModal(true)}
          >
            ➕ Agregar Documento
          </button>
        </div>
      </div>

      {documentos.length === 0 ? (
        <div className="empty-state">
          <p>No tienes documentos registrados</p>
          <button 
            className="btn-primary"
            onClick={() => setShowModal(true)}
          >
            Agregar Primer Documento
          </button>
        </div>
      ) : (
        <div className="documentos-list">
          {documentos.map((doc) => (
            <div key={doc.id} className="documento-card">
              <div className="documento-info">
                <h3>{doc.tipo_documento_nombre}</h3>
                <p><strong>Número:</strong> {doc.numero_documento || 'N/A'}</p>
                <p><strong>Emisión:</strong> {doc.fecha_emision ? new Date(doc.fecha_emision).toLocaleDateString() : 'N/A'}</p>
                <p><strong>Vencimiento:</strong> {doc.fecha_vencimiento ? new Date(doc.fecha_vencimiento).toLocaleDateString() : 'N/A'}</p>
                <p><strong>Estado:</strong> {getEstadoBadge(doc.estado)}</p>
              </div>
              <div className="documento-actions">
                <button
                  className="btn-pdf"
                  onClick={() => generarPDF(doc.id)}
                >
                  📄 Generar PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => !submitting && setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Agregar Documento</h2>
              <button 
                className="modal-close"
                onClick={() => !submitting && setShowModal(false)}
                disabled={submitting}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="documento-form">
              <div className="form-group">
                <label>Tipo de Documento *</label>
                <select
                  name="tipo_documento_id"
                  value={formData.tipo_documento_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccione un tipo</option>
                  {tiposDocumentos.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Número de Documento</label>
                <input
                  type="text"
                  name="numero_documento"
                  value={formData.numero_documento}
                  onChange={handleInputChange}
                  placeholder="Ej: 123456789"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha de Emisión</label>
                  <input
                    type="date"
                    name="fecha_emision"
                    value={formData.fecha_emision}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Fecha de Vencimiento</label>
                  <input
                    type="date"
                    name="fecha_vencimiento"
                    value={formData.fecha_vencimiento}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Observaciones</label>
                <textarea
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Notas adicionales sobre el documento..."
                />
              </div>

              <div className="form-group">
                <label>Imágenes * (Múltiples)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  capture="environment"
                />
                <small>Puede seleccionar múltiples imágenes. Use la cámara del celular o seleccione desde la galería.</small>
              </div>

              {previews.length > 0 && (
                <div className="imagenes-preview">
                  <h4>Imágenes Agregadas ({previews.length})</h4>
                  <div className="preview-grid">
                    {previews.map((preview, index) => (
                      <div key={index} className="preview-item">
                        <img src={preview} alt={`Preview ${index + 1}`} />
                        <button
                          type="button"
                          className="btn-remove-image"
                          onClick={() => removeImage(index)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting || imagenes.length === 0}
                >
                  {submitting ? 'Guardando...' : 'Finalizar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosOperador;

